import { ArrowDown, ArrowUp, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@nexus/ui";
import type { ResolvedMetric } from "./chartModel.js";

interface MetricPillProps {
  metric: ResolvedMetric;
  canRemove: boolean;
  isActiveSort: boolean;
  onToggleSort: (id: string) => void;
  onRemove: (id: string) => void;
}

/** One selected metric: color swatch, label, sort toggle, remove. */
export function MetricPill({ metric, canRemove, isActiveSort, onToggleSort, onRemove }: MetricPillProps) {
  const { t } = useTranslation();
  // Inactive pills always promote to desc on first click — show that, not a leftover asc.
  const effectiveSort = isActiveSort ? metric.sort : "desc";
  const SortIcon = effectiveSort === "desc" ? ArrowDown : ArrowUp;
  const sortDirection = t(
    effectiveSort === "desc" ? "overview.chart.sort.descending" : "overview.chart.sort.ascending",
    effectiveSort === "desc" ? "descending" : "ascending",
  );
  return (
    <div
      data-testid={`metric-pill-${metric.id}`}
      className="flex h-[37px] items-center gap-2 rounded-[10px] border border-border bg-background px-2"
    >
      <span
        className="size-2.5 shrink-0 rounded-full"
        style={{ background: metric.colorVar }}
        aria-hidden
      />
      <span className="text-[13px] font-bold tracking-[-0.08px] text-[color:var(--chart-axis-text)]">
        {metric.label}
      </span>

      <button
        type="button"
        aria-label={t("overview.chart.sort.metric", {
          metric: metric.label,
          direction: sortDirection,
          defaultValue: `Sort ${metric.label} ${sortDirection}`,
        })}
        aria-pressed={isActiveSort}
        data-testid={`metric-pill-${metric.id}-sort`}
        onClick={() => onToggleSort(metric.id)}
        className={cn(
          "flex size-6 items-center justify-center rounded-md hover:bg-muted hover:text-foreground",
          isActiveSort ? "bg-muted text-foreground" : "text-muted-foreground",
        )}
      >
        <SortIcon className="size-3.5" />
      </button>

      <button
        type="button"
        aria-label={t("overview.chart.removeMetric", { metric: metric.label, defaultValue: `Remove ${metric.label}` })}
        data-testid={`metric-pill-${metric.id}-remove`}
        disabled={!canRemove}
        onClick={() => onRemove(metric.id)}
        className={cn(
          "flex size-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground",
          !canRemove && "cursor-not-allowed opacity-40 hover:bg-transparent hover:text-muted-foreground",
        )}
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}
